import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { PiNotePencilBold } from "react-icons/pi"

import { useRecaptchaObserver } from "@fuse/hooks/useRecaptchaObserver"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import EmailComponent from "@fuse/sharedComponents/emailComponent"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import NameComponent from "@fuse/sharedComponents/nameComponent"
import PhoneNumberField from "@fuse/sharedComponents/phoneNumberInputField"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import VerifyOtpComponent from "@fuse/sharedComponents/verifyOTPComponent"
import { countryData, CountryType } from "@letshang/assets"
import { EButtonType, TWebUser } from "@letshang/constants"
import { formatPhoneNumber, isPhoneNumberValid } from "@letshang/utils"
import * as Sentry from "@sentry/react"
import { signInWithPhoneNumber } from "firebase/auth"
import { isEmpty } from "lodash-es"
import { useRecoilState } from "recoil"

import { userState } from "@fuse/recoil/signup"

import { auth } from "@firebaseConfig/clientApp"

import { sentryLog } from "@fuse/utils/common"

import RecaptchaStateProvider, {
  recaptchaErrorhandler,
  useRecaptcha,
} from "../signUp/RecaptchaStateProvider"

interface IFastSignUpComponent {
  isOpen: boolean
  setOpenState: (val: boolean) => void
  title: string
  phoneNumberToBePrefilled?: string
  showTitleAsIs?: boolean
  cannotDismissPopup?: boolean
  from?: "createEvent" | "joinEvent"
  verifyEmail?: boolean
}

export const CoreFastSignupComponent = ({
  isOpen,
  setOpenState,
  step,
  setStep,
  title = 'Join "The coolest pool party"',
  phoneNumberToBePrefilled,
  showTitleAsIs = false,
  cannotDismissPopup = false,
  from = "joinEvent",
  verifyEmail = false,
}: IFastSignUpComponent & {
  setStep: React.Dispatch<SetStateAction<number>>
  step: number
}) => {
  const handleStopPropagation = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
    },
    [],
  )

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [country, setCountry] = useState<CountryType>(countryData[4])
  const [, setUser] = useRecoilState(userState)
  const recaptchaRef = useRef(null)

  const isHavePrefilledPhone = !isEmpty(phoneNumberToBePrefilled)
  const {
    embedTheRecaptchaElement,
    clearRecaptchaToken,
    initializeTheRecaptchaVerifier,
    verifier,
    recaptchaVerifiedState,
    isProcessing,
  } = useRecaptcha()

  const onChallengeHide = () => {
    setIsLoading(false)
    isProcessing.current = false
  }

  // its the captcha challenge observer
  useRecaptchaObserver({
    onChallengeHide,
  })

  const reInitializeTheRecaptchaVerifier = useCallback(() => {
    initializeTheRecaptchaVerifier(recaptchaRef)
  }, [initializeTheRecaptchaVerifier])

  const signInTheUser = useCallback(
    async (formatted: string) => {
      return signInWithPhoneNumber(
        auth,
        country.dial_code + formatted.replace(/-/g, ""),
        verifier.current,
      )
    },
    [country.dial_code, verifier],
  )

  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const phoneNumberLengthCheckerRef = useRef<any>()

  const signIn = useCallback(
    async (phone: string) => {
      console.debug("signIn", isProcessing.current)

      if (isHavePrefilledPhone && phone !== phoneNumberToBePrefilled) {
        showErrorToast(
          "Wrong phone number. Make sure you use the one linked to the ticket.",
        )
        return
      }

      if (!recaptchaVerifiedState?.current) {
        try {
          console.debug("after expiry , when signing in recaptchaVerifiedState")
          const wait = await new Promise<{
            isReloaded: boolean
            message?: string
          }>((resolve, reject) => {
            showErrorToast("Your verification expired.", {
              text2: "Please reload the page to sign in again.",
              buttonText: "Reload Page",
              onClose: () => {
                reject({
                  isReloaded: false,
                  code: "auth/recaptcha-expired/re-auth-denied",
                  message: "User didn't re-initailize the verifier instance",
                })
              },
              onButtonClick: () => {
                reInitializeTheRecaptchaVerifier()
                if (verifier.current) {
                  verifier.current?.render() // to render the re-captcha again
                  resolve({
                    isReloaded: true,
                  })
                } else {
                  reject({
                    isReloaded: false,
                    code: "auth/recaptcha-expired/verifier-not-available",
                    message:
                      "Verifier instance is not available even after re-initialization",
                  })
                }
              },
            })
          })

          if (!wait?.isReloaded) {
            return
          }
        } catch (err: unknown) {
          const error = err as {
            isReloaded: false
            message: string
            code: string
          }
          Sentry.captureMessage(
            JSON.stringify({
              type: "failed/re-initialization",
              code: error.code,
              error:
                error?.message ||
                "Error occured while reloading the verifier instance",
            }),
          )
          console.error(err)
          console.debug("failed after expiry approach:", err)
        }
      }

      const formatted = formatPhoneNumber(phone, country.code)

      console.debug(
        "isPhoneNumberValid",
        isPhoneNumberValid(phone, country.code),
      )
      if (!isPhoneNumberValid(phone, country.code)) {
        return alert("Invalid phone number")
      }

      try {
        setIsLoading(true)
        console.debug("SignIn user started")

        const confirmation = await signInTheUser(formatted)

        setUser((prev: TWebUser) => ({
          ...prev,
          phone: phone,
          dial_code: country.dial_code,
          verificationId: confirmation.verificationId,
          country: countryData.find(
            (item) => item.dial_code === country.dial_code,
          )?.code,
        }))
        setStep(2)
      } catch (error: any) {
        console.debug("SignIn user Failed")
        console.error(error)
        sentryLog({ error: error, component: "FastSignup" })
        recaptchaErrorhandler(error)
      } finally {
        setIsLoading(false)
      }
    },
    [phoneNumber, country.dial_code, phoneNumberToBePrefilled],
  )

  useEffect(() => {
    if (isOpen) {
      initializeTheRecaptchaVerifier(recaptchaRef)
    } else {
      setStep(1)
      console.debug("clearing the verifier")
      verifier.current?.clear()
    }

    return () => {
      // Cleanup on unmount
      clearRecaptchaToken()
    }
  }, [isOpen])

  const verifyValidPhone = useCallback(
    (phoneNumber, countryCode) => {
      let validLength = false
      let isValidPhoneNumber = false
      if (
        phoneNumberLengthCheckerRef.current?.checkPhoneLength &&
        countryCode
      ) {
        validLength =
          phoneNumberLengthCheckerRef.current?.checkPhoneLength(phoneNumber)
      }

      if (validLength && countryCode) {
        isValidPhoneNumber = isPhoneNumberValid(phoneNumber, countryCode)
      }
      return isValidPhoneNumber
    },
    [phoneNumber, country?.code],
  )

  const onClickSendCode = useCallback(async () => {
    console.debug("Button is Clicked to send code")
    if (isProcessing.current || isLoading) {
      console.debug("Send code is already in process")
      return
    }

    console.debug("Entered Send code further process")
    const existingRecaptcha = document.querySelector("#recaptcha-container")

    if (!existingRecaptcha) {
      embedTheRecaptchaElement(recaptchaRef)
    }

    isProcessing.current = true
    try {
      console.debug("going to verify recaptcha")
      const verifiedState = await verifier.current?.verify()
      if (verifiedState) {
        setTimeout(() => signIn(phoneNumber), 500)
      }
    } catch (err) {
      console.debug("Failed at verifier level")
      console.error(err)
      showErrorToast(
        "Error occured while verifying recaptcha. Please try again later",
      )
      sentryLog({ error: err, component: "FastSignup" })
    } finally {
      console.log("executing finally")
      isProcessing.current = false
    }
  }, [
    isProcessing,
    isLoading,
    phoneNumber,
    signIn,
    embedTheRecaptchaElement,
    verifier,
  ])

  if (!isOpen) {
    return null
  }

  return (
    <div
      className="fixed top-0 bottom-0 left-0 right-0 bg-[#00000080] flex w-full justify-center items-end md:items-center z-50"
      onClick={() => {
        if (!cannotDismissPopup) {
          setOpenState(!isOpen)
        }
      }}
    >
      <div ref={recaptchaRef} className="recaptcha-container"></div>
      <div className="relative z-50">{isLoading && <LoadingSpinner />}</div>

      <div className="w-full md:max-w-xl z-50" onClick={handleStopPropagation}>
        <BlurWrapper
          className={`px-7 py-14 md:px-14 md:py-14 rounded-tr-3xl rounded-tl-3xl md:rounded-xl flex flex-col gap-6 ${
            step === 4 ? "!px-0 !py-0 !bg-transparent" : ""
          }`}
          overlayColorDefault="dark"
          overlayOpacityDefault="60"
        >
          {step !== 4 && (
            <div className="font-sans text-3xl font-semibold text-white ">
              {showTitleAsIs ? title : `Join "${title}"`}
            </div>
          )}
          {step !== 3 && step !== 4 ? (
            <div className="flex gap-6 flex-col-reverse">
              {step === 1 && (
                <div className="flex gap-6 flex-col-reverse pt-12">
                  <Button
                    variant={EButtonType.solid}
                    className="py-2"
                    onClick={onClickSendCode}
                    isLoading={isLoading}
                    disabled={!verifyValidPhone(phoneNumber, country?.code)}
                  >
                    <div className="text-white font-semibold font-sans">
                      Send code
                    </div>
                  </Button>

                  <div className="text-center text-xs text-white/35 font-medium hover:cursor-default">
                    By continuing, you agree to our
                    <span
                      className="underline-offset-2 underline hover:cursor-pointer"
                      onClick={() =>
                        window.open(
                          "https://pages.flycricket.io/walkme/terms.html",
                          "_blank",
                        )
                      }
                    >{` Terms `}</span>
                    and
                    <span
                      className="underline-offset-2 underline hover:cursor-pointer"
                      onClick={() =>
                        window.open(
                          "https://pages.flycricket.io/walkme/privacy.html",
                          "_blank",
                        )
                      }
                    >{` Privacy Policy `}</span>
                    and consent to receive text messages from us and hosts. We
                    will send you an SMS for verification.
                  </div>
                </div>
              )}
              {step === 2 && (
                <VerifyOtpComponent
                  signInTheUser={signInTheUser}
                  setStep={setStep}
                  closeModal={() => setOpenState(!isOpen)}
                  reInitializeTheRecaptchaVerifier={
                    reInitializeTheRecaptchaVerifier
                  }
                />
              )}

              <PhoneNumberField
                ref={phoneNumberLengthCheckerRef}
                setVal={setPhoneNumber}
                readOnly={step !== 1}
                country={country}
                setCountry={setCountry}
                {...(isHavePrefilledPhone ? { phoneNumberToBePrefilled } : {})}
              >
                {step === 2 && (
                  <div
                    className="mr-2 cursor-pointer"
                    onClick={() => {
                      setIsLoading(false)
                      clearRecaptchaToken()
                      initializeTheRecaptchaVerifier(recaptchaRef)
                      setStep(1)
                    }}
                  >
                    <PiNotePencilBold className=" text-white text-lg" />
                  </div>
                )}
              </PhoneNumberField>
            </div>
          ) : (
            <>
              {step === 3 && (
                <div className="">
                  <NameComponent
                    from={from}
                    closeModal={() => setOpenState(!isOpen)}
                    verifyEmail={verifyEmail}
                    setStep={setStep}
                  />
                </div>
              )}
            </>
          )}
          {step === 4 && (
            <div className="">
              <EmailComponent
                from={from}
                closeModal={() => setOpenState(!isOpen)}
              />
            </div>
          )}
        </BlurWrapper>
      </div>
    </div>
  )
}
